import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Signature, FilePdf, CheckCircle, BookOpenText } from "@phosphor-icons/react";
import { openAuthFile } from "../../lib/fileOpen";
import { SectionText, SectionTable, fmtDate } from "./hseUtils";

/** My Work → Safety: HSE documents issued to me to read & sign, with an in-app reader. */
export function HseReadSignList({ testid = "in-house-hse-docs" }) {
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => API.get("/hse/documents/mine").then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);
  const sign = async (d) => {
    try { const { data } = await API.post(`/hse/documents/${d.id}/acknowledge`); toast.success(data.message); setOpen(null); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const pending = (docs || []).filter((d) => !d.mine?.acknowledged_at);
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid={testid}>
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-2"><BookOpenText className="h-5 w-5" /> H&amp;S documents {pending.length > 0 && <span className="text-xs font-medium text-amber-600" data-testid={`${testid}-pending`}>· {pending.length} to read &amp; sign</span>}</h2>
      {!docs ? <p className="text-sm text-muted-foreground">Loading…</p> : !docs.length ? <p className="text-sm text-muted-foreground">No policies or procedures have been issued to you yet.</p> : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm" data-testid={`${testid}-row-${d.id}`} data-signed={!!d.mine?.acknowledged_at}>
              {d.mine?.acknowledged_at ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" /> : <Signature size={18} className="text-amber-600 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{d.title}</p>
                <p className="text-[11px] text-muted-foreground">{d.type_label} · v{d.version} · {d.mine?.acknowledged_at ? `signed ${fmtDate(d.mine.acknowledged_at)}` : "awaiting your signature"}</p>
              </div>
              <Button size="sm" variant={d.mine?.acknowledged_at ? "ghost" : "default"} className="h-8" onClick={() => setOpen(d)} data-testid={`${testid}-open-${d.id}`}>{d.mine?.acknowledged_at ? "Read" : "Read & sign"}</Button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`${testid}-reader`}>
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.title}</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">{open.type_label} · version {open.version} · approved {fmtDate(open.approved_at)} by {open.approved_by_name}</p>
              <div className="space-y-4">
                {open.type_sections.map((s, i) => (
                  <section key={s.key}>
                    <h3 className="text-sm font-semibold mb-1">{i + 1}. {s.title}</h3>
                    {s.kind === "table" ? <SectionTable rows={open.sections?.[s.key] || []} columns={s.columns} /> : <SectionText text={open.sections?.[s.key]} />}
                  </section>
                ))}
              </div>
              <DialogFooter className="gap-2 sm:justify-between">
                <Button variant="outline" onClick={() => openAuthFile(`/hse/documents/${open.id}/pdf`)} data-testid={`${testid}-pdf`}><FilePdf size={14} className="mr-1" /> PDF</Button>
                {open.mine?.acknowledged_at ? <span className="text-sm text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={16} weight="fill" /> Signed {fmtDate(open.mine.acknowledged_at)}</span>
                  : <Button onClick={() => sign(open)} className="min-h-[44px]" data-testid={`${testid}-sign`}><Signature size={16} className="mr-1" /> I've read &amp; understood this</Button>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
